import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Star, Clock, ChevronDown, ChevronUp, Search, Loader2, Check } from 'lucide-react';
import clsx from 'clsx';
import type { FeaturedSummary } from '../../types/types';
import type { FeaturedCandidate } from '../../hooks/useFeaturedManagement';

interface Props {
  theme: 'light' | 'dark' | 'high-contrast';
  summary: FeaturedSummary | null;
  // Whether the user has clicked "Gestionar" — when true, the parent grid
  // filters down to feature-eligible properties and each card shows the
  // star action. Banner expands inline to show the name picker + purchases.
  manageMode: boolean;
  onToggleManageMode: () => void;
  // Name typeahead: find a publishable property by name/code and feature it
  // directly, so the user doesn't have to hunt for its card in the grid.
  searchCandidates: (query: string) => Promise<FeaturedCandidate[]>;
  onActivate: (propertyId: string) => Promise<boolean>;
}

const cycleLabel: Record<string, string> = {
  mensual: 'Mensual',
  semestral: 'Semestral',
  anual: 'Anual',
};

function daysUntil(dateIso: string): number {
  const ms = new Date(dateIso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function formatDate(dateIso: string): string {
  return new Date(dateIso).toLocaleDateString('es-GT', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Type-to-find picker: search publishable properties by name/code and feature
// the chosen one in one click (assigns the soonest-expiring free slot).
const FeaturedPicker: React.FC<{
  theme: 'light' | 'dark' | 'high-contrast';
  searchCandidates: (query: string) => Promise<FeaturedCandidate[]>;
  onActivate: (propertyId: string) => Promise<boolean>;
}> = ({ theme, searchCandidates, onActivate }) => {
  const isLight = theme !== 'dark';
  const [q, setQ] = useState('');
  const [results, setResults] = useState<FeaturedCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounced search; the cancelled flag drops stale responses that resolve
  // out of order so the dropdown always reflects the latest query.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      const r = await searchCandidates(term);
      if (!cancelled) { setResults(r); setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, searchCandidates]);

  // Click outside closes the results dropdown.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setResults([]);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = useCallback(async (c: FeaturedCandidate) => {
    setActivatingId(c.id);
    const ok = await onActivate(c.id);
    setActivatingId(null);
    if (ok) {
      setDone(c.title);
      setQ('');
      setResults([]);
    }
  }, [onActivate]);

  return (
    <div className="mb-3" ref={boxRef}>
      <label className={clsx('block text-[10px] font-bold uppercase tracking-wider mb-1.5', isLight ? 'text-amber-700/80' : 'text-amber-300/70')}>
        Destacar una propiedad
      </label>
      <div className="relative">
        <div className={clsx('flex items-center gap-2 rounded-lg border px-3 py-2', isLight ? 'bg-white border-amber-200' : 'bg-amber-500/5 border-amber-500/30')}>
          <Search size={15} className={isLight ? 'text-amber-500' : 'text-amber-400'} />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setDone(null); }}
            placeholder="Escribe el nombre o código de la propiedad…"
            className={clsx('flex-1 bg-transparent text-sm focus:outline-none', isLight ? 'text-slate-900 placeholder:text-amber-700/40' : 'text-white placeholder:text-amber-300/40')}
          />
          {loading && <Loader2 size={14} className="animate-spin text-amber-500" />}
        </div>

        {results.length > 0 && (
          <div className={clsx('absolute z-20 left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden max-h-72 overflow-y-auto', isLight ? 'bg-white border-amber-200' : 'bg-slate-900 border-amber-500/30')}>
            {results.map((c) => (
              <button
                key={c.id}
                onClick={() => pick(c)}
                disabled={activatingId !== null}
                className={clsx('w-full flex items-center gap-3 px-3 py-2 text-left transition-colors disabled:opacity-50', isLight ? 'hover:bg-amber-50' : 'hover:bg-amber-500/10')}
              >
                {c.cover_photo_url
                  ? <img src={c.cover_photo_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                  : <div className={clsx('w-10 h-10 rounded shrink-0 flex items-center justify-center', isLight ? 'bg-amber-100 text-amber-500' : 'bg-amber-500/10 text-amber-400')}><Star size={14} /></div>}
                <div className="flex-1 min-w-0">
                  <p className={clsx('text-sm font-semibold truncate', isLight ? 'text-slate-900' : 'text-white')}>{c.title}</p>
                  <p className={clsx('text-[11px] truncate', isLight ? 'text-slate-500' : 'text-slate-400')}>
                    {[c.ii_id, c.city_name].filter(Boolean).join(' · ') || 'Publicada'}
                  </p>
                </div>
                {activatingId === c.id
                  ? <Loader2 size={15} className="animate-spin text-amber-500 shrink-0" />
                  : <Star size={15} className="text-amber-400 shrink-0" />}
              </button>
            ))}
          </div>
        )}

        {!loading && q.trim().length >= 2 && results.length === 0 && !done && (
          <p className={clsx('text-[11px] mt-1.5', isLight ? 'text-amber-700/70' : 'text-amber-300/60')}>
            No se encontraron propiedades publicadas con ese nombre.
          </p>
        )}
      </div>

      {done && (
        <p className={clsx('text-xs font-semibold mt-2 flex items-center gap-1.5', isLight ? 'text-emerald-600' : 'text-emerald-400')}>
          <Check size={13} /> «{done}» se destacó correctamente.
        </p>
      )}
    </div>
  );
};

export const FeaturedManager: React.FC<Props> = ({ theme, summary, manageMode, onToggleManageMode, searchCandidates, onActivate }) => {
  const isLight = theme !== 'dark';
  const totals = summary?.totals;
  const hasPurchases = (summary?.purchases?.length ?? 0) > 0;

  if (!summary || !hasPurchases) return null;

  const available = totals?.total_available ?? 0;
  const used = totals?.total_used ?? 0;
  const total = totals?.total_slots ?? 0;

  return (
    <div
      className={clsx(
        'rounded-xl border transition-all',
        isLight ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/10 border-amber-500/20'
      )}
    >
      {/* Banner — always visible */}
      <button
        onClick={onToggleManageMode}
        className={clsx(
          'w-full flex items-center justify-between gap-3 px-4 py-3 transition-colors',
          isLight ? 'hover:bg-amber-100' : 'hover:bg-amber-500/15',
          manageMode ? 'rounded-t-xl' : 'rounded-xl'
        )}
      >
        <div className="flex items-center gap-3">
          <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center', isLight ? 'bg-amber-200 text-amber-700' : 'bg-amber-500/20 text-amber-400')}>
            <Star size={18} fill="currentColor" />
          </div>
          <div className="text-left">
            <p className={clsx('text-sm font-bold', isLight ? 'text-amber-900' : 'text-amber-200')}>
              {available > 0
                ? `Tienes ${available} destaque${available === 1 ? '' : 's'} disponible${available === 1 ? '' : 's'} para activar`
                : `Todos tus destaques están activos (${used}/${total})`}
            </p>
            <p className={clsx('text-xs', isLight ? 'text-amber-700' : 'text-amber-300/80')}>
              {used} activo{used === 1 ? '' : 's'} · {total} slot{total === 1 ? '' : 's'} total
              {manageMode ? (available > 0 ? ' · Busca una propiedad por nombre para destacarla' : ' · Selecciona una propiedad publicada para destacar') : ''}
            </p>
          </div>
        </div>
        <span className={clsx('text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5', isLight ? 'bg-amber-600 text-white' : 'bg-amber-500 text-white')}>
          {manageMode ? (<>Salir <ChevronUp size={12} /></>) : (<>Gestionar <ChevronDown size={12} /></>)}
        </span>
      </button>

      {/* Inline-expanded details — name picker (when slots free) + purchases
          summary. The properties themselves stay in the main grid below. */}
      {manageMode && (
        <div className={clsx('px-4 pb-4 pt-3 border-t', isLight ? 'border-amber-200/70' : 'border-amber-500/20')}>
          {available > 0 && (
            <FeaturedPicker theme={theme} searchCandidates={searchCandidates} onActivate={onActivate} />
          )}
          <h4 className={clsx('text-[10px] font-bold uppercase tracking-wider mb-2', isLight ? 'text-amber-700/80' : 'text-amber-300/70')}>
            Compras Activas
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {summary.purchases.map(pur => (
              <div
                key={pur.id}
                className={clsx(
                  'rounded-lg border p-2.5 flex items-center gap-3',
                  isLight ? 'border-amber-200/70 bg-white/60' : 'border-amber-500/20 bg-amber-500/5'
                )}
              >
                <div className="text-xl shrink-0">{pur.package_icon || '⭐'}</div>
                <div className="flex-1 min-w-0">
                  <p className={clsx('text-xs font-bold truncate', isLight ? 'text-amber-900' : 'text-amber-100')}>{pur.package_title}</p>
                  <p className={clsx('text-[10px] flex items-center gap-1', isLight ? 'text-amber-700' : 'text-amber-300/80')}>
                    <Clock size={10} />
                    {cycleLabel[pur.billing_cycle] ?? pur.billing_cycle} · {formatDate(pur.expire_date)} · {daysUntil(pur.expire_date)}d
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className={clsx('text-xs font-bold', isLight ? 'text-amber-900' : 'text-amber-100')}>{pur.used_slots}/{pur.slot_count}</p>
                  <p className={clsx('text-[9px] uppercase tracking-wider', pur.available_slots > 0 ? 'text-emerald-500' : isLight ? 'text-amber-600/60' : 'text-amber-300/50')}>
                    {pur.available_slots > 0 ? `${pur.available_slots} libre${pur.available_slots === 1 ? '' : 's'}` : 'lleno'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
